/**
 * Pure logic behind the Food page: targets progress, one-tap repeat logging,
 * and the trailing week strip. Kept DOM-free so each contract is unit-tested.
 */
import type {
  NutritionDay,
  NutritionSummary,
  NutritionTargets,
  RecentFood,
  SaveMealRequest,
} from '../api.js';

const DAY_MS = 86_400_000;

export interface TargetProgress {
  key: 'calories' | 'protein_g' | 'fiber_g' | 'sugar_g';
  label: string;
  value: number;
  target: number;
  pct: number;
  over: boolean;
  kind: 'goal' | 'ceiling';
}

const TARGET_ROWS: { key: TargetProgress['key']; label: string; kind: TargetProgress['kind'] }[] = [
  { key: 'calories', label: 'Calories', kind: 'goal' },
  { key: 'protein_g', label: 'Protein', kind: 'goal' },
  { key: 'fiber_g', label: 'Fiber', kind: 'goal' },
  { key: 'sugar_g', label: 'Sugar', kind: 'ceiling' },
];

/** Read the `nutrition_targets` setting: non-object → {}; keep finite positive keys. */
export function parseTargets(raw: unknown): NutritionTargets {
  if (!raw || typeof raw !== 'object') return {};
  const src = raw as Record<string, unknown>;
  const out: NutritionTargets = {};
  for (const key of ['calories', 'protein_g', 'fiber_g', 'sugar_g'] as const) {
    const v = src[key];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) out[key] = v;
  }
  return out;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/**
 * One progress row per target present in `targets`. `pct` is clamped to 0..1;
 * sugar is a ceiling (`over` when value exceeds target), the others are
 * floors/goals (never flagged `over`).
 */
export function targetProgress(
  summary: NutritionSummary,
  targets: NutritionTargets,
): TargetProgress[] {
  const rows: TargetProgress[] = [];
  for (const row of TARGET_ROWS) {
    const target = targets[row.key];
    if (target == null || !Number.isFinite(target) || target <= 0) continue;
    const value = Number(summary[row.key]) || 0;
    rows.push({
      key: row.key,
      label: row.label,
      value,
      target,
      pct: clamp01(value / target),
      over: row.kind === 'ceiling' ? value > target : false,
      kind: row.kind,
    });
  }
  return rows;
}

/**
 * A `POST /api/meals` body that repeats a recent food: a single item named after
 * the food, carrying its last-logged portion and macros verbatim, at `now`.
 */
export function repeatMealPayload(item: RecentFood, now: Date = new Date()): SaveMealRequest {
  return {
    at: now.toISOString(),
    name: item.name,
    items: [
      {
        name: item.name,
        qty: item.lastQty,
        unit: item.lastUnit,
        calories: item.calories,
        protein_g: item.protein_g,
        carbs_g: item.carbs_g,
        fat_g: item.fat_g,
        fiber_g: item.fiber_g,
        sugar_g: item.sugar_g,
        caffeine_mg: item.caffeine_mg,
        source: 'manual',
      },
    ],
  };
}

/** Local YYYY-MM-DD for a Date (calendar day in the viewer's timezone). */
export function localYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface WeekDay {
  date: string;
  label: string;
  calories: number | null;
  protein_g: number | null;
}

/**
 * Exactly 7 cells ending today (local), oldest first. Days with no logged data
 * are filled with nulls; `label` is the single-letter weekday initial.
 */
export function weekDays(days: NutritionDay[], now: Date = new Date()): WeekDay[] {
  const byDate = new Map(days.map((d) => [d.date, d]));
  const out: WeekDay[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now.getTime() - i * DAY_MS);
    const date = localYmd(d);
    const hit = byDate.get(date);
    out.push({
      date,
      label: d.toLocaleDateString(undefined, { weekday: 'narrow' }),
      calories: hit ? hit.calories : null,
      protein_g: hit ? hit.protein_g : null,
    });
  }
  return out;
}
